import {
  toCompanyCreateDTO,
  toCompanyInputCreateDTO,
  toCompanyDetailCreateDTO,
  fromCompanyDTO,
  fromCompanyInputDTO,
  fromCompanyDetailDTO,
  toCompanyPatchCantonDTO,
  toCompanyDetailPatchDTO,
  toCompanyInputSubmitDTO,
  toCompanyInputReopenDTO,
} from "../dto/companyMapper";
import { fromOverheadsDTO } from "../dto/overheadsMapper";

/**
 * Company Service
 * - Company CRUD, detail patching, and the "new template" build flow.
 */
export function createCompanyService({ apiClient, apiUrl, interpolateURL, logError, debug, trusteeName }) {
  const useDebug = !!debug?.useDebugMode;
  const createAborter = () => new AbortController();
  const fetchWithAwait = (params) => apiClient({ ...params, mode: "resolution" });

  // ───────────────────────────────────────────────────────────────────────────────
  // Low-level steps
  // ───────────────────────────────────────────────────────────────────────────────

  async function createCompany({ companyHash, cantonId, aborter, requestHash }) {
    const endpoint = "company";
    let companyId = null;
    await fetchWithAwait({
      method: "POST",
      url: interpolateURL({ apiUrl, endpoint, id: null, omitLanguage: false }),
      body: toCompanyCreateDTO(companyHash, cantonId),
      hash: requestHash,
      onSuccess: ({ data }) => (companyId = fromCompanyDTO(data).id),
      onError: (err) => logError(endpoint, err, aborter),
      ignoreError: !useDebug,
    });
    return companyId;
  }

  async function createCompanyInput({ surveyId, companyId, aborter, requestHash }) {
    const endpoint = "company-input";
    let companyInput = {};

    await fetchWithAwait({
      method: "POST",
      url: interpolateURL({ apiUrl, endpoint, id: null, omitLanguage: false }),
      body: toCompanyInputCreateDTO(surveyId, companyId),
      hash: requestHash,
      onSuccess: ({ data }) => (companyInput = fromCompanyInputDTO(data)),
      onError: (err) => logError(endpoint, err, aborter),
      ignoreError: !useDebug,
    });

    return companyInput;
  }

  async function createCompanyDetail({ companyInputId, nogaId, officeSizeId, aborter, requestHash }) {
    const endpoint = "company-detail";
    let companyDetail = {};

    await fetchWithAwait({
      method: "POST",
      url: interpolateURL({ apiUrl, endpoint, id: null, omitLanguage: false }),
      body: toCompanyDetailCreateDTO(companyInputId, nogaId, officeSizeId),
      hash: requestHash,
      onSuccess: ({ data }) => (companyDetail = fromCompanyDetailDTO(data)),
      onError: (err) => logError(endpoint, err, aborter),
      ignoreError: !useDebug,
    });

    return companyDetail;
  }

  async function listCompanyOverheads({ translate, aborter, companyInput, requestHash }) {
    const endpoint = `company-overhead/company-input/${companyInput.id}`;
    let companyOverheads = [];

    await fetchWithAwait({
      method: "GET",
      url: interpolateURL({ apiUrl, endpoint, id: null, omitLanguage: false }),
      hash: requestHash,
      onSuccess: ({ data }) => (companyOverheads = fromOverheadsDTO(data, translate)),
      onError: (err) => logError(endpoint, err, aborter),
      ignoreError: !useDebug,
    });

    return companyOverheads;
  }

  // ───────────────────────────────────────────────────────────────────────────────
  // Public: orchestrator
  // ───────────────────────────────────────────────────────────────────────────────

  /**
   * Build a new company template model (company, input, detail, overheads).
   */
  async function buildNewTemplateModel({ companyHash, requestHash, nogaCodeObj, cantonObj, officeSizeObj, survey, translate, companyId }) {
    const aborter = createAborter();
    const model = {
      companyHash,
      companyId: null,
      nogaCodeObj,
      cantonObj,
      officeSizeObj,
      survey,
      companyInput: {},
      companyDetail: {},
      trustee: { name: trusteeName },
      companyOverheads: [],
      employees: [],
    };

    if (useDebug) console.log("=== Build New Template Model (company, input, detail, overheads) ===");
    if (companyId) {
      model.companyId = companyId;
    } else {
      model.companyId = await createCompany({ companyHash, cantonId: cantonObj.id, aborter, requestHash });
    }
    model.companyInput = await createCompanyInput({ surveyId: survey.id, companyId: model.companyId, aborter, requestHash });
    model.companyDetail = await createCompanyDetail({
      companyInputId: model.companyInput.id,
      nogaId: nogaCodeObj.id,
      officeSizeId: officeSizeObj.id,
      aborter,
      requestHash,
    });
    let companyInput = model.companyInput;
    model.companyOverheads = await listCompanyOverheads({ translate, aborter, companyInput, requestHash });

    if (aborter.signal.aborted) return { ok: false, data: undefined, error: new Error("Aborted") };
    return { ok: true, data: model, error: null };
  }

  /**
   * Update company/canton or company-detail/noga based on the slice.
   */
  async function patchCompanyOrDetail({
    stateSlice,
    companyDetailId,
    companyId,
    companyInput,
    payloadObj,
    selectedNoga,
    selectedOfficeSize,
    requestHash,
  }) {
    const isCanton = stateSlice === "canton";
    const endpoint = isCanton ? "company" : "company-detail";
    const url = interpolateURL({
      apiUrl,
      endpoint,
      id: isCanton ? companyId : companyDetailId,
      omitLanguage: false,
    });
    const body = isCanton
      ? toCompanyPatchCantonDTO(requestHash, payloadObj.id)
      : toCompanyDetailPatchDTO(
          companyInput.id,
          stateSlice === "noga" ? payloadObj.id : selectedNoga?.id,
          stateSlice === "office-size" ? payloadObj.id : selectedOfficeSize?.id
        );

    try {
      let patched;
      await fetchWithAwait({
        method: "PATCH",
        url,
        body,
        hash: requestHash,
        onSuccess: ({ data }) => (patched = data),
        onError: (err) => {
          throw err;
        },
        ignoreError: false,
      });
      return { ok: true, data: patched, error: null };
    } catch (error) {
      logError(endpoint, error);
      return { ok: false, data: undefined, error };
    }
  }

  /**
   * Submit company input for review.
   */
  async function submitCompanyInput({ surveyId, companyInput, companyId, clientReply, requestHash }) {
    const endpoint = "company-input";
    try {
      await fetchWithAwait({
        method: "PATCH",
        url: interpolateURL({ apiUrl, endpoint, id: companyInput.id, omitLanguage: false }),
        body: toCompanyInputSubmitDTO(companyId, surveyId, companyInput.collecting, clientReply),
        hash: requestHash,
        onError: (err) => {
          throw err;
        },
        ignoreError: !useDebug,
      });
      return { ok: true, data: true, error: null };
    } catch (error) {
      logError(endpoint, error);
      return { ok: false, data: undefined, error };
    }
  }

  async function reopenCompanyInput({ surveyId, companyInput, companyId, requestHash }) {
    const endpoint = "company-input";
    try {
      await fetchWithAwait({
        method: "PATCH",
        url: interpolateURL({ apiUrl, endpoint, id: companyInput.id, omitLanguage: false }),
        body: toCompanyInputReopenDTO(companyId, surveyId),
        hash: requestHash,
        onError: (err) => {
          throw err;
        },
        ignoreError: !useDebug,
      });

      return { ok: true, data: true, error: null };
    } catch (error) {
      logError(endpoint, error);
      return { ok: false, data: undefined, error };
    }
  }

  return {
    buildNewTemplateModel,
    patchCompanyOrDetail,
    submitCompanyInput,
    reopenCompanyInput,
  };
}
